import {
  AnalyticsSavedFilterDocument,
  savedDocumentToFilterInputs,
} from "../../types/analytics/AnalyticsSavedFilter";
import AnalyticsFilterInputs from "../../types/analytics/AnalyticsFilterInputs";
import { pageSupportsPointInTime } from "./pointInTimeFilterHelpers";

export type ApplySavedFilterToPageResult = {
  inputs: AnalyticsFilterInputs;
  skippedJourneyLabels: string[];
  isPartial: boolean;
};

export type PageJourneyFallback = Pick<
  AnalyticsFilterInputs,
  "allJourneys" | "journeyKeys" | "dateRange"
>;

/**
 * Maps a saved filter document onto the current page's available journeys.
 * Audience always applies. Named journeys use intersection; allJourneys means
 * all journeys on this page (no skip warning).
 * Pages that cannot honour point-in-time (e.g. Usage) strip pointInTimeDate.
 */
export function applySavedFilterToPage(args: {
  doc: AnalyticsSavedFilterDocument;
  availableJourneyKeys: string[];
  /** Current page journey/date selection when the save omits journeys or none match. */
  pageFallback: PageJourneyFallback;
  /** Page identifier or saved-filter page key; used to strip PIT on Usage. */
  pageIdentifier?: string | null;
}): ApplySavedFilterToPageResult {
  const { doc, availableJourneyKeys, pageFallback, pageIdentifier } = args;
  const base = savedDocumentToFilterInputs(doc);
  const available = new Set(availableJourneyKeys);
  const stripPointInTime = !pageSupportsPointInTime(pageIdentifier);

  const withPagePointInTime = (
    inputs: AnalyticsFilterInputs
  ): AnalyticsFilterInputs =>
    stripPointInTime ? { ...inputs, pointInTimeDate: null } : inputs;

  if (doc.journey == null) {
    return {
      inputs: withPagePointInTime({
        ...base,
        allJourneys: pageFallback.allJourneys,
        journeyKeys: pageFallback.journeyKeys,
        dateRange: pageFallback.dateRange,
      }),
      skippedJourneyLabels: [],
      isPartial: false,
    };
  }

  if (doc.journey.allJourneys) {
    return {
      inputs: withPagePointInTime({
        ...base,
        allJourneys: available.size > 0,
        journeyKeys: availableJourneyKeys.slice(),
      }),
      skippedJourneyLabels: [],
      isPartial: false,
    };
  }

  const savedJourneys = doc.journey.journeys ?? [];
  const matchedKeys = savedJourneys
    .map((j) => j.key)
    .filter((key) => available.has(key));
  const skippedJourneyLabels = savedJourneys
    .filter((j) => !available.has(j.key))
    .map((j) => j.label || j.key);

  if (matchedKeys.length === 0) {
    return {
      inputs: withPagePointInTime({
        ...base,
        allJourneys: pageFallback.allJourneys,
        journeyKeys: pageFallback.journeyKeys,
      }),
      skippedJourneyLabels:
        skippedJourneyLabels.length > 0
          ? skippedJourneyLabels
          : savedJourneys.map((j) => j.label || j.key),
      isPartial: true,
    };
  }

  const allMatched =
    matchedKeys.length === available.size && available.size > 0;

  return {
    inputs: withPagePointInTime({
      ...base,
      allJourneys: allMatched,
      journeyKeys: matchedKeys,
    }),
    skippedJourneyLabels,
    isPartial: skippedJourneyLabels.length > 0,
  };
}
